import React, { ReactNode, useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  FaChevronLeft,
  FaChevronRight,
  // FaTachometerAlt,
  FaConciergeBell,
  FaClipboardList,
  FaThLarge,
  FaBars,
  FaBed,
  FaCog,
  FaBook,
  FaUsers,
  FaBoxes,
  FaUserTie,
  FaSignOutAlt,
  FaCreditCard,
  FaChartLine,
  FaStar,
  FaToolbox,
} from "react-icons/fa";
// import Logo from "../assets/logo.png"
import Logo from "../assets/hotel_logo.png";
import { useCart } from "../context/CartContext";
import { initAllStores } from "../utils/cache";
import ResetPasswordModal from "../Modal/staff/ResetPasswordModal";
import type User from "../models/User";
import { errorMessage, sendRequest } from "../utils/api";
import { useModal } from "../context/ConfirmModalContext";
import { useToast } from "../context/ToastContext";
import ChatBubble from "./ChatBubble";
import { getUserHotel, terminal } from "../utils/user-helper";

interface DashboardLayoutProps {
  children: ReactNode;
}

const menuItems = [
  { name: "Dashboard", path: "/dashboard" },
  { name: "Services", path: "/service", isParent: true },
  { name: "Orders", path: "/orders" },
  { name: "Rooms", path: "/rooms" },
  { name: "Bookings", path: "/bookings" },
  { name: "Guests", path: "/guests" },
  { name: "Reviews", path: "/reviews" },
  { name: "Inventory", path: "/inventory" },
  { name: "Expenditures", path: "/expenditures" },
  { name: "Sales", path: "/sales" },
  { name: "Staff", path: "/staffs" },
  { name: "Maintenances", path: "/maintenances" },
  // { name: "Help & Support", path: "/help" },
  { name: "Settings", path: "/settings" },
  { name: "Logout", path: "/logout" },
];

const serviceSubItems = [
  { name: "Foods", path: "/service/foods" },
  { name: "Drinks", path: "/service/drinks" },
  { name: "Laundries", path: "/service/laundries" },
  { name: "Games", path: "/service/games" },
];

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false); // Mobile toggle
  const { cart } = useCart();
  const totalSelected = cart.length;
  const [user, setUser] = useState<{ name: string; email: string } | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [allowChat, setAllowChat] = useState(true);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const [allowMenu, setAllowMenu] = useState(menuItems);

  const [terminalId] = useState(() => terminal());

  const navigate = useNavigate();

  const { openModal, closeModal } = useModal();
  const { showToast } = useToast();


  //   useEffect(() => {
  //   if (terminalId) {
  //     localStorage.setItem("terminal", terminalId);
  //   }
  // }, [terminalId]);

  // Load user from cache
  useEffect(() => {
    (async () => {
      // const cache = await initAllStores();
      // const savedUser = await cache.getBy("data", { id: "user" });

      const data = await getUserHotel();
      // const hotel: Hotel | null = await cache.getBy("data", { id: "hotel" });

      if (!data) {
        return openModal(
          "Session Expired ⚠️",
          "We couldn’t find your user information. Please log in again to continue.",
          () => {
            closeModal();
            window.location.href = "/login";
          },
          false,
          "Login Again"
        );
      }

      const { user, hotel } = data;

      const chatBubbleSetting = user?.settings?.find(st => st.name);
      const allowChatByUser = !!(chatBubbleSetting && chatBubbleSetting.value === "show");
      const allowChatByHotel = hotel?.preference?.chat ?? true;
      setAllowChat(allowChatByHotel && allowChatByUser);


      let name: string = user.firstName + " " + user.lastName;
      name = name.length > 20 ? name.slice(0, 15) + "..." : name;
      setUser({ ...user, name });

      // Compute accessible menus
      const menuViews = (user?.permissions ?? [])
        .filter((perm: any) => perm.type === "view" && perm.value === true)
        .map((perm: any) => perm.name.toLowerCase());

      // Extend logic for grouped permissions
      const expandedViews = new Set(menuViews);

      // If user has "bookings" permission, they can also view "rooms"
      if (expandedViews.has("bookings")) {
        expandedViews.add("rooms");
      }

      // If user has "orders" permission, they can also view "services"
      if (expandedViews.has("orders")) {
        expandedViews.add("services");
      }

      // Filter menus based on expanded permissions
      const filteredMenus = menuItems.filter((item) =>
        expandedViews.has(item.name.toLowerCase())
      );

      setAllowMenu(filteredMenus);
    })();
  }, []);

  useEffect(() => {
    const forceResetPassword = async () => {
      const cache = await initAllStores();
      const user: User | null = await cache.getBy("data", { id: "user" });
      if (!user) await handleLogout();

      if (user?.forceReset) setIsModalOpen(true);
    };
    forceResetPassword();
  }, []);

  const handleLogout = async () => {
    const cache = await initAllStores();
    //const { clearCart } = useCart();
    try {
      await sendRequest("DELETE", "/auth/logout");
      // await cache.clearAll(["drinks", "foods"]);
    } catch (err: any) {
      console.log(err);
    }
    localStorage.removeItem("authToken");
    localStorage.removeItem("terminal");
    //clearCart();
    navigate("/login");
  };

  const handleReset = async (newPassword: string) => {
    const cache = await initAllStores();
    try {
      await sendRequest("PUT", "/auth/reset-password/confirm", { newPassword });
      await cache.updateManyBy("data", ["user"], { forceReset: false });
      setIsModalOpen(false);
      showToast("Password Reset Successful", { type: "success" });

    } catch (error: any) {
      const err = errorMessage(error.status);
      if (err) openModal(err.title, err.msg, closeModal, false, "Close");
      console.log(error);
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <div
        className={`bg-gray-200 ${isCollapsed ? "w-20" : "w-64"
          } flex flex-col justify-between fixed md:relative h-full z-20 transform transition-all duration-300 ease-in-out
    ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0 p-2 md:p-4`}
      >
        {/* Top Section */}
        <div className="relative mb-4 flex items-center justify-between">
          {/* Logo — top-left */}
          <div className="flex items-center pl-1 pt-2">
            <img
              src={Logo}
              alt="Logo"
              className={`transition-all duration-300 ease-in-out rounded-md ${isCollapsed ? "w-8 h-8" : "w-10 h-10"
                }`}
            />
            {!isCollapsed && (
              <span className="ml-2 font-semibold text-gray-800 text-base">
                {/* Wiventory */}
                Wiiliams Court
              </span>
            )}
          </div>

          {/* Collapse Toggle - top right */}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="absolute right-0 top-2 p-2 rounded-md hover:bg-gray-300 transition"
            title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {isCollapsed ? (
              <span className="text-gray-700 text-lg">{FaChevronRight({})}</span>
            ) : (
              <span className="text-gray-700 text-lg">{FaChevronLeft({})}</span>
            )}
          </button>
        </div>

        {/* Scrollable Menu */}
        <div className="flex-1 overflow-y-auto p-1 md:p-2 space-y-2">
          {allowMenu.map((item) => {
            const label = !isCollapsed ? item.name : "";

            // Assign icons for each menu
            const getIcon = (name: string) => {
              switch (name.toLowerCase()) {
                case "dashboard":
                  return FaThLarge({});
                case "services":
                  return FaConciergeBell({});
                case "orders":
                  return FaClipboardList({});
                case "rooms":
                  return FaBed({});
                case "bookings":
                  return FaBook({});
                case "guests":
                  return FaUsers({});
                case "inventory":
                  return FaBoxes({});
                case "staff":
                  return FaUserTie({});
                case "roster":
                  return FaClipboardList({});
                case "settings":
                  return FaCog({});
                case "maintenances":
                  return FaToolbox({});
                case "expenditures":
                  return FaCreditCard({});
                case "sales":
                  return FaChartLine({});
                case "logout":
                  return FaSignOutAlt({});
                case "reviews":
                  return FaStar({});
                default:
                  return FaClipboardList({});
              }
            };

            if (item.isParent) {
              return (
                <div key={item.name}>
                  <button
                    onClick={() => setIsServicesOpen(!isServicesOpen)}
                    className={`w-full flex items-center justify-between px-2 py-2 rounded ${isServicesOpen
                      ? "bg-blue-600 text-white"
                      : "bg-gray-200 hover:bg-gray-300"
                      }`}
                  >
                    <div className="flex items-center space-x-2">
                      {getIcon(item.name)}
                      {!isCollapsed && <span>{item.name}</span>}
                    </div>
                    {!isCollapsed && <span>{isServicesOpen ? "▼" : "➤"}</span>}
                  </button>

                  {isServicesOpen && !isCollapsed && (
                    <div className="ml-4 mt-1 space-y-1">
                      {serviceSubItems.map((sub) => (
                        <NavLink
                          key={sub.name}
                          to={sub.path}
                          className={({ isActive }) =>
                            `block px-2 py-1 md:px-3 md:py-2 rounded text-sm ${isActive
                              ? "bg-blue-500 text-white"
                              : "bg-gray-100 hover:bg-gray-200"
                            }`
                          }
                        >
                          {sub.name}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            if (item.name === "Logout") {
              return (
                <button
                  key={item.name}
                  onClick={handleLogout}
                  className="w-full flex items-center justify-between px-2 py-2 rounded bg-gray-200 hover:bg-gray-300"
                >
                  <div className="flex items-center space-x-2">
                    {getIcon(item.name)}
                    {!isCollapsed && <span>{item.name}</span>}
                  </div>
                </button>
              );
            }

            return (
              <NavLink
                key={item.name}
                to={item.path}
                className={({ isActive }) =>
                  `w-full flex items-center justify-between px-2 py-2 rounded relative ${isActive
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 hover:bg-gray-300"
                  }`
                }
              >
                <div className="flex items-center space-x-2">
                  {getIcon(item.name)}
                  {!isCollapsed && <span>{
                    item.name === "Dashboard" ? `Dashboard ${terminalId.toUpperCase()}` : item.name}</span>
                  }
                </div>

                {item.name === "Orders" && totalSelected > 0 && (
                  <span className="absolute right-2 top-1 bg-red-500 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full">
                    {totalSelected}
                  </span>
                )}
              </NavLink>
            );
          })}
        </div>

        {/* User Profile */}
        {!isCollapsed && (
          <div
            onClick={() => navigate("/profile")}
            className="p-2 md:p-4 flex items-center space-x-2 md:space-x-3 bg-gray-100 rounded-lg shadow-sm cursor-pointer">
            <div className="w-10 h-10 rounded-full bg-gray-400 flex items-center justify-center text-white font-semibold">
              {user?.name[0].toLocaleUpperCase()}
            </div>

            <div>
              <div
                className="font-bold text-black text-sm md:text-base"
                title={user?.name}
              >
                {(user?.name ?? "").length > 15
                  ? `${(user?.name ?? "").slice(0, 15)}...`
                  : user?.name ?? ""}
              </div>
              <div className="text-xs md:text-sm text-black" title={user?.email}>
                {(user?.email ?? "").length > 15
                  ? `${(user?.email ?? "").slice(0, 15)}...`
                  : user?.email ?? ""}
              </div>
            </div>
          </div>
        )}
      </div>


      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-30 z-10 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Mobile Header */}
        {/* <div className="flex items-center justify-between bg-white p-2 md:hidden shadow-md">
          <h1 className="text-lg md:text-xl font-bold">Dashboard</h1>
          <button onClick={() => setSidebarOpen(!sidebarOpen)}>
            {FaBars({ size: 22 })}
          </button>
        </div> */}
        <div className="relative flex items-center bg-white p-2 md:hidden shadow-md">
          {/* Hamburger */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="z-10 ml-1"
          >
            {FaBars({ size: 22 })}
          </button>

          {/* Centered title */}
          <h1 className="absolute left-1/2 -translate-x-1/2 text-lg font-bold">
            William's Court Hotel
          </h1>
        </div>

        {/* Children */}
        <div className="flex-1 overflow-auto md:p-1">
          <ResetPasswordModal
            isOpen={isModalOpen}
            showCloseBtn={false}
            onClose={() => setIsModalOpen(false)}
            onReset={handleReset}
          />

          {/* Floating Chat Bubble */}
          {/* {allowChat && < ChatBubble />} */}

          {children}
        </div>
        {/* <div className="flex-1 overflow-auto p-1 md:p-6">{children}</div> */}
      </div>
    </div>
  );

};

export default DashboardLayout;